/**
 * Oturum bazlı persona geri bildirim durumu.
 *
 * Transkriptteki HER tur bu bilgiye ihtiyaç duyuyor (bu konuşmacı kalıcı bir
 * kimlik mi, bu tura zaten oy verilmiş mi). Her `TranscriptTurn` kendi
 * isteğini atsaydı 20 turluk bir tartışma 20 kez aynı ucu çağırırdı; bu
 * yüzden durum paylaşımlı bir store'da toplanır ve oturum başına TEK istek
 * yapılır.
 *
 * Yükleme tembeldir: kütüphane personası kullanılmayan tartışmalarda uç bir
 * kez çağrılır, boş harita döner ve oylama arayüzü hiç render edilmez.
 */
import { useEffect } from "react";
import { create } from "zustand";
import type { FeedbackVerdict } from "../domain/personaLibrary";
import { fetchSessionFeedback } from "../transport/personaLibraryClient";

interface SessionFeedbackStore {
  /** Hangi oturum için yüklendiği — oturum değişince yeniden çekilir. */
  sessionId: string | null;
  loading: boolean;
  /** runtime persona id → kütüphane persona id. */
  libraryBySpeaker: Record<string, string>;
  /** turn id → verilmiş oy. */
  verdictByTurn: Record<string, FeedbackVerdict>;

  ensure(sessionId: string): Promise<void>;
  setLocalVerdict(turnId: string, verdict: FeedbackVerdict): void;
  reset(): void;
}

const useStore = create<SessionFeedbackStore>((set, get) => ({
  sessionId: null,
  loading: false,
  libraryBySpeaker: {},
  verdictByTurn: {},

  ensure: async (sessionId) => {
    const state = get();
    if (state.loading) return;
    if (state.sessionId === sessionId) return;
    set({ loading: true, sessionId, libraryBySpeaker: {}, verdictByTurn: {} });
    try {
      const data = await fetchSessionFeedback(sessionId);
      const verdictByTurn: Record<string, FeedbackVerdict> = {};
      for (const f of data.feedback) {
        if (f.turnId) verdictByTurn[f.turnId] = f.verdict;
      }
      set({
        loading: false,
        libraryBySpeaker: data.libraryBySpeaker,
        verdictByTurn,
      });
    } catch {
      // Kütüphane kapalıysa (404/503) ya da ağ hatasında sessizce boş kal —
      // oylama arayüzü görünmez, tartışma etkilenmez.
      set({ loading: false });
    }
  },

  setLocalVerdict: (turnId, verdict) => {
    set((s) => ({ verdictByTurn: { ...s.verdictByTurn, [turnId]: verdict } }));
  },

  reset: () =>
    set({ sessionId: null, loading: false, libraryBySpeaker: {}, verdictByTurn: {} }),
}));

/**
 * Transkript kökünden bir kez çağrılır; oturum kimliği değiştikçe tazeler.
 */
export function useSessionPersonaFeedbackLoader(sessionId: string | null): void {
  const ensure = useStore((s) => s.ensure);
  useEffect(() => {
    if (!sessionId) return;
    void ensure(sessionId);
  }, [sessionId, ensure]);
}

/** Tur bileşenlerinin okuduğu ince arayüz. */
export function useTurnFeedback(turnId: string, speakerId: string) {
  const libraryPersonaId = useStore((s) => s.libraryBySpeaker[speakerId]);
  const verdict = useStore((s) => s.verdictByTurn[turnId]);
  const setLocalVerdict = useStore((s) => s.setLocalVerdict);
  return { libraryPersonaId, verdict, setLocalVerdict };
}

export const sessionPersonaFeedbackStore = useStore;
